import React from 'react';
import {ReferenceObject} from 'popper.js';
import {
  ClickAwayListener,
  Grow,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Theme,
  Typography
} from '@material-ui/core';
import {BORDER_PRIMARY_COLOR} from 'src/style/variable';

/* Interfaces for custom pop over */
export interface CustomPopOverListItemType {
  name?: any;
  id?: string | number;
  icon?: Element | any;
  route?: string;
  label?: string | any;
  menuPermissionName?: string | any;
  disabled?: boolean;
}
interface CustomPopOverTypes {
  open: boolean;
  anchorEl: null | ReferenceObject | (() => ReferenceObject);
  listItems: Array<CustomPopOverListItemType>;
  position:
    | 'bottom'
    | 'bottom-end'
    | 'bottom-start'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top'
    | undefined;
  handleClose?(): void;
  handleListItemClick?(
    item: CustomPopOverListItemType,
    event?: React.MouseEvent<EventTarget>
  ): void;
  enablePortal?: boolean;
  className?: string;
}

const itemLabelStyles = makeStyles((theme: Theme) => ({
  menuItemWrap: {
    overflow: 'auto',
    '& li': {
      paddingTop: '10px',
      paddingBottom: '10px',
      borderBottom: `1px solid ${BORDER_PRIMARY_COLOR}`
    },
    '& li:last-child': {
      borderBottom: 'none'
    }
  },
  menuItemTextStyle: {
    flexGrow: 1,
    overflow: 'hidden',
    width: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  iconWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    minWidth: '40px'
  },
  menuListStyle: {
    paddingBottom: 0
  },
  menuMaxWidth: {
    maxWidth: '20rem'
  },
  // style to assign max height to popper component
  popperHeight: {
    '& ul': {
      '& div': {
        maxHeight: '25rem'
      }
    }
  }
}));

const CustomPopOver = (props: CustomPopOverTypes): React.ReactElement => {
  const {
    anchorEl,
    listItems,
    open,
    position,
    handleClose,
    handleListItemClick,
    enablePortal,
    className = ''
  } = props;

  const handleCloseEvent = (
    event: React.MouseEvent<EventTarget>,
    item?: CustomPopOverListItemType
  ) => {
    if (typeof handleListItemClick === 'function' && item) {
      handleListItemClick(item, event);
    }
    if (typeof handleClose === 'function') {
      handleClose();
    }
  };

  const classes = itemLabelStyles();

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
    }
  }
  return (
    <Popper
      open={open}
      className={`${classes.popperHeight} ${className}`}
      // Added modifiers prop to flip the custompoover based on the available space
      modifiers={{
        flip: {
          enabled: true
        },
        preventOverflow: {
          enabled: true,
          boundariesElement: 'scrollParent'
        }
      }}
      style={{zIndex: 9999}}
      anchorEl={anchorEl}
      role={undefined}
      transition
      placement={position}
      disablePortal={!enablePortal}>
      {({TransitionProps, placement}) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === 'bottom' ? 'center top' : 'center bottom'
          }}>
          <Paper elevation={3}>
            <ClickAwayListener onClickAway={handleCloseEvent}>
              <MenuList
                autoFocusItem={open}
                id="custom-popover-list-grow"
                className={[classes.menuListStyle, classes.menuMaxWidth].join(
                  ' '
                )}
                onKeyDown={handleListKeyDown}>
                <div className={classes.menuItemWrap}>
                  {Array.isArray(listItems) && listItems.length > 0 ? (
                    listItems.map((item: any, index: any) => (
                      <MenuItem
                        key={item.id}
                        style={{display: item.id === '' ? 'none' : 'flex'}}
                        disabled={item.disabled || false}
                        onClick={event => handleCloseEvent(event, item)}>
                        <Typography
                          data-test-id={`pcl-dashboard-popOver${index}`}
                          className={classes.menuItemTextStyle}
                          variant="body1"
                          color={'primary'}>
                          {item?.label || item.name}
                        </Typography>
                        {item?.icon && (
                          <div className={classes.iconWrapper}>
                            {item?.icon}
                          </div>
                        )}
                      </MenuItem>
                    ))
                  ) : (
                    <div />
                  )}
                </div>
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

export default CustomPopOver;
